import { useState } from "react";
import {
  AddCircleSharp,
  ArrowDown,
  ArrowUp,
  LayersOutline,
} from "react-ionicons";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FONTSIZE, mainCryptoName } from "../../../constants";
import { PAGE_NAME, UserModelData } from "../../../types";
import AllCryptos from "../../components/AllCryptos";
import RecentUsersInteractedWith from "../../components/RecentUsersInteractedWith";
import Text from "../../components/ThemedText";
import useThemeHook from "../../hooks/useThemeHook";
import { useTranslationHook } from "../../hooks/useTranslationHook";
import { THEME_COLORS } from "../../themes/DarkLight";
import piNetworkAdsManager from "../../utils/PiNetworkAdsManager";

export default function WalletPage() {
  const userData: UserModelData = useSelector(
    (state) => (state as any)?.actualUser
  );
  const piPrice = useSelector((state) => (state as any)?.piPrice);

  const getTranslationText = useTranslationHook();

  const piTotalWorth = (
    (userData?.user?.picoinBalance ?? 0) * (piPrice ?? 0)
  )?.toFixed(2);

  const theme = useThemeHook();
  
  const navigate = useNavigate();

  const onOptionPress = (path: string, stateData?: any) =>
    navigate(path, { state: stateData });

  const options = [
    {
      onPress: () => onOptionPress(PAGE_NAME.SEND_CRYPTO),
      id: 1,
      iconName: (
        <ArrowUp
          height={FONTSIZE.SECONDARY}
          width={FONTSIZE.SECONDARY}
          color={THEME_COLORS[theme].gray}
        />
      ),
      title: getTranslationText("send"),
    },
    {
      onPress: () => onOptionPress(PAGE_NAME.RECEIVE_CRYPTO),
      id: 2,
      iconName: (
        <ArrowDown
          height={FONTSIZE.SECONDARY}
          width={FONTSIZE.SECONDARY}
          color={THEME_COLORS[theme].gray}
        />
      ),
      title: getTranslationText("receive"),
    },
    {
      onPress: () => onOptionPress(PAGE_NAME.DEPOSIT),
      id: 3,
      iconName: (
        <AddCircleSharp
          height={FONTSIZE.SECONDARY}
          width={FONTSIZE.SECONDARY}
          color={THEME_COLORS[theme].gray}
        />
      ),
      title: getTranslationText("deposit"),
    },
    {
      onPress: () =>
        onOptionPress(PAGE_NAME.HYSTORY_PAGE, {
          getCryptoTransactions: true,
        }),
      id: 4,
      iconName: (
        <LayersOutline
          height={FONTSIZE.SECONDARY}
          width={FONTSIZE.SECONDARY}
          color={THEME_COLORS[theme].gray}
        />
      ),
      title: getTranslationText("history"),
    },
  ];

  const watchAds = () => {
    piNetworkAdsManager.handleRewardedAdFlowFront(userData?.user?.id!);
  };

  return (
    <div className={`min-h-screen ${theme === "dark" ? "bg-gray-900" : "bg-white"}`}>
      <div className="max-w-md mx-auto px-3 py-3 pb-24 space-y-3">
        
        {/* Balance Card - Clean with Blue Neon Accents */}
        <div className={`relative overflow-hidden border rounded-2xl p-5 ${
          theme === "dark" 
            ? "bg-gray-800 border-gray-700" 
            : "bg-white border-gray-200"
        }`}>
          {/* Blue neon gradient effects in background */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-blue-500/10 to-transparent rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-blue-400/10 to-transparent rounded-full blur-2xl"></div>
          
          {/* Header */}
          <div className="relative flex items-center justify-between mb-4">
            <span className={`text-xs font-medium ${
              theme === "dark" ? "text-gray-400" : "text-gray-600"
            }`}>
              {getTranslationText("this_is_your_usd_balance")}
            </span>
            <span className="text-xl">💸</span>
          </div>

          {/* Balance Amount */}
          <div className="relative text-center py-4">
            <div className={`text-5xl font-bold mb-2 ${
              theme === "dark" ? "text-white" : "text-gray-900"
            }`}>
              ${piTotalWorth}
            </div>
            <div className={`text-sm font-medium ${
              theme === "dark" ? "text-gray-500" : "text-gray-400"
            }`}>
              USD
            </div>
          </div>

          {/* Description */}
          <div className={`relative text-xs mt-3 pt-3 border-t ${
            theme === "dark" ? "text-gray-500 border-gray-700" : "text-gray-500 border-gray-100"
          }`}>
            {getTranslationText("this_is_your_usd_balance_desc")}
          </div>
        </div>

        {/* Action Buttons - Compact Grid */}
        <div className="flex items-center justify-between gap-2 px-1">
          {options?.map((op) => (
            <button
              key={op.id}
              onClick={op.onPress}
              className={`flex flex-col items-center gap-1.5 p-2 rounded-xl transition-colors ${
                theme === "dark"
                  ? "hover:bg-gray-800 active:bg-gray-700"
                  : "hover:bg-gray-50 active:bg-gray-100"
              }`}
            >
              <div className={`w-11 h-11 rounded-full flex items-center justify-center ${
                theme === "dark" ? "bg-gray-800" : "bg-gray-100"
              }`}>
                {op.iconName}
              </div>
              <span className={`text-[10px] font-medium ${
                theme === "dark" ? "text-gray-400" : "text-gray-600"
              }`}>
                {op.title}
              </span>
            </button>
          ))}
        </div>

        {/* Watch Ads Banner - Simple */}
        <button 
          onClick={watchAds} 
          className={`w-full border rounded-2xl p-4 transition-all active:scale-[0.98] ${
            theme === "dark"
              ? "bg-gray-800 border-gray-700 hover:bg-gray-750"
              : "bg-gray-50 border-gray-200 hover:bg-gray-100"
          }`}
        >
          <div className="flex items-center justify-center gap-2">
            <span className="text-xl">🎬</span>
            <span className={`font-semibold text-sm ${
              theme === "dark" ? "text-white" : "text-gray-900"
            }`}>
              {getTranslationText("watch_ads__to_get_ath",{amount:1, mainCryptoName:mainCryptoName}) || "Watch Ads to Earn Pi!!"}
            </span>
          </div>
        </button>

        {/* Recent Users & All Cryptos */}
        <div className="pt-2 space-y-3">
          <RecentUsersInteractedWith />
          <AllCryptos />
        </div>
      </div>
    </div>
  );
}